// **HostGathering** — the projected join-window screen (`client-home`'s "the gathering screen
// presents the join code, the room filling, and a Start that nothing gates" requirement; sketch
// `prototype/sketches/tournament-big-screen/host-gathering.html`).
//
// A composable component (`client-home`'s "the host surfaces are composable components"):
// takes its transport, its join code and its QR encoder as inputs, constructs no HTTP client, no
// credential store, and touches the `qrcode` package nowhere in this file — the composition root
// is the only place that does. It owns one small poll loop and nothing else stateful.

import { useEffect, useRef, useState } from 'react';
import type { HostMembersDto, HostTournamentTransport } from '../../net/rounds-host-http';
import { joinUrl, type QrEncoder } from '../../app/rounds/qr-join';
import { Button } from '../components/core/Button';

export interface HostGatheringProps {
  /** Host-only reads and the Start transition. */
  transport: HostTournamentTransport;
  /** The plaintext join code — from the create response or host-scoped session storage, never
   * from a route (no route returns it). */
  joinCode: string;
  /** The origin a projected QR should point at — the room's own reachable address. */
  origin: string;
  /** Generates the QR locally. No network request is ever issued for it. */
  encodeQr: QrEncoder;
  /** How often the joined list polls. */
  pollIntervalMs?: number;
}

const DEFAULT_POLL_MS = 3000;

function ordinalLabel(preview: string | undefined): string {
  switch (preview) {
    case 'published':
      return 'Preview ready';
    case 'running':
      return 'Preview running…';
    case 'failed':
      return 'Preview failed — phones will start empty';
    default:
      return 'Preview pending…';
  }
}

export function HostGathering({
  transport,
  joinCode,
  origin,
  encodeQr,
  pollIntervalMs = DEFAULT_POLL_MS,
}: HostGatheringProps) {
  const [members, setMembers] = useState<HostMembersDto | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  /** Disabled the instant Start is pressed, re-enabled only by a fresh mount — the "safe to
   * press twice" rule needs no server round trip to hold, since the phase itself will move the
   * screen on once the report lands. */
  const [starting, setStarting] = useState(false);
  const [started, setStarted] = useState(false);
  const [startFailed, setStartFailed] = useState(false);
  const liveRef = useRef(true);

  useEffect(() => {
    liveRef.current = true;
    return () => {
      liveRef.current = false;
    };
  }, []);

  // The QR is generated **once**, locally, from the join code alone — never re-encoded on a
  // members poll, and never a network request.
  useEffect(() => {
    let cancelled = false;
    void encodeQr(joinUrl(origin, joinCode)).then((dataUrl) => {
      if (!cancelled) {
        setQrDataUrl(dataUrl);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [encodeQr, origin, joinCode]);

  // The joined list: polled, never streamed.
  useEffect(() => {
    let cancelled = false;
    const poll = (): void => {
      void transport.readMembers().then(
        (dto) => {
          if (!cancelled && liveRef.current) {
            setMembers(dto);
          }
        },
        () => undefined,
      );
    };
    poll();
    const id = setInterval(poll, pollIntervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [transport, pollIntervalMs]);

  // The host membership is spectator-kind (design **D4**) and never occupies a roster slot, so
  // it does not belong in the joined count or list even where a fixture's roster carries it
  // for other purposes — the real `/members` route never sends one at all.
  const joined = (members?.members ?? []).filter((member) => member.memberKind === 'competitor');
  const capacity = members?.rosterCapacity ?? null;
  const full = capacity !== null && joined.length >= capacity;

  function pressStart(): void {
    if (starting || started) {
      return;
    }
    setStarting(true);
    setStartFailed(false);
    void transport.start().then((outcome) => {
      if (!liveRef.current) {
        return;
      }
      if (outcome === 'refused') {
        // A retried Start under a tournament already past the window reports the started phase
        // rather than a failure — this arm is reachable only for a genuine transport failure.
        setStarting(false);
        setStartFailed(true);
        return;
      }
      // Confirmed. The screen stays disabled — in the rounds host flow the phase read is what
      // leaves it — but the label must say the round is underway: a host whose surface is not
      // phase-driven (the demo shell advances on the presenter's key) otherwise watches
      // "Starting…" forever with no evidence the click landed.
      setStarted(true);
    });
  }

  return (
    <div data-testid="host-gathering" style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 24 }}>
      {!full ? (
        <div data-testid="host-gathering-qr" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          {qrDataUrl !== null && <img src={qrDataUrl} alt="Scan to join" width={220} height={220} />}
          <span data-testid="host-gathering-code" style={{ fontFamily: 'var(--font-mono)', fontSize: 22, letterSpacing: 3 }}>
            {joinCode}
          </span>
        </div>
      ) : (
        <div data-testid="host-gathering-full" role="status">
          The roster is full — every colony slot is taken.
        </div>
      )}

      <div data-testid="host-gathering-count">
        {joined.length}
        {capacity !== null ? ` / ${capacity}` : ''} joined
      </div>

      <div data-testid="host-gathering-preview-readiness">{ordinalLabel(members?.preview)}</div>

      <ol data-testid="host-gathering-joined-list" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {joined
          .slice()
          .sort((a, b) => a.joinOrdinal - b.joinOrdinal)
          .map((member) => (
            <li key={member.joinOrdinal} data-testid={`host-gathering-member-${member.joinOrdinal}`}>
              {member.displayName}
            </li>
          ))}
      </ol>

      <Button
        size="lg"
        data-testid="host-gathering-start"
        disabled={starting || started}
        onClick={pressStart}
      >
        {started ? 'Started — round underway' : starting ? 'Starting…' : 'Start'}
      </Button>
      {startFailed && (
        <div data-testid="host-gathering-start-failed" role="alert">
          Couldn’t reach the hive — try again.
        </div>
      )}
    </div>
  );
}
